import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import DateField from '../../components/Fields/DateField';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import NumberField from '../../components/Fields/NumberField';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import AddressTypeModel from '../../models/AddressTypeModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { MenuItemModel } from '../../types/MenuItemModel';
import { DataResult } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';


interface MenuItemEditModel {
    id: number

    title: string
    simpleLineIcon?: string
    navigateUrl?: string
    parentMenuItemId?: number
    displayOrder?: number
    beginDate: string
    endDate?: string
    applicationFunctionId?: number

    concurrencyId: number
}

const MenuItemView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<DataResult<MenuItemEditModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);
    const [formData, setFormData] = useState<MenuItemEditModel>();

    const applicationFunctions = useSelectListOptions('api/v1/selectlist/Applicationfunctions', '-- Select application function');
    const menuItems = useSelectListOptions('api/v1/selectlist/MenuItems', '-- Select menuitem');

    let idParams: IdParams = useParams();
    let menuItemId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        menuItemId = parseInt(idParams.id);
    }

    useEffect(() => {
        if (menuItemId !== undefined) {
            DataService.getById<MenuItemEditModel>('api/v1/menuitems/', menuItemId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [menuItemId]);

    function submit() {
        if (formData) {
            DataService
                .update<MenuItemEditModel>(`api/v1/menuitems/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        setFormData(response.data);
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'title', label: 'Title', validationRules: [{ type: 'isRequired' }] },
            { name: 'navigateUrl', label: 'Navigate URL'},
            { name: 'displayOrder', label: 'Display order' },
            { name: 'applicationFunctionId', label: 'Application function' },
            { name: 'simpleLineIcon', label: 'Icon' },
            { name: 'parentMenuItemId', label: 'Parent menuitem' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (menuItemId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditMenuItem)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Menu item' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={3}>
                            <Authorize applicationFunction={ApplicationFunctions.EditMenuItem}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <TextboxField {...getFieldProps('title')} />
                            <TextboxField {...getFieldProps('navigateUrl')} />
                            <NumberField {...getFieldProps('displayOrder')} />
                            <SelectListField {...getFieldProps('applicationFunctionId')} options={applicationFunctions} />
                            <TextboxField {...getFieldProps('simpleLineIcon')} />
                            <SelectListField {...getFieldProps('parentMenuItemId')} options={menuItems} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
        </div>
    );
}
export default MenuItemView;